// src/components/BuyerProfileCard.js
import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Button,
  Divider,
  Modal,
  TextField,
  Stack,
  Select,
  InputLabel,
  OutlinedInput,
  Chip,
  MenuItem,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { http } from "../lib/axios";
import ProfileImageManager from "./ProfileImageManager";
import { ART_TYPE_OPTIONS } from '../utils/hostProfileOptions';
import AgreementAcceptModal from "../components/AgreementAcceptModal";
import { getAgreementStatus } from "../lib/apiAgreements";

const BUYER_AGREEMENT_CONFIG = {
  agreementCode: "BUYER_PLATFORM",
  title: "Buyer Membership Agreement",
  workflowContext: { workflow: "buyer_profile" },
};

function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/media/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^https?:\/\/[^/]+/, '');
  }
  const trimmed = url.replace(/^\.?\/*/, '');
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

export default function BuyerProfileCard({ user, isSelf = false }) {
  const [open, setOpen] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(null);
  const [agreementAvailable, setAgreementAvailable] = useState(false);
  const [agreementCheckError, setAgreementCheckError] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    address: '',
    art_preferences: [],
  });

  const [profileImages, setProfileImages] = useState([]); // only for self

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]); // [{src, caption}]
  const [viewerIndex, setViewerIndex] = useState(0);

  const openViewerWithImages = (imgs, idx = 0) => {
    if (!Array.isArray(imgs) || !imgs.length) return;
    setViewerImages(imgs);
    setViewerIndex(idx);
    setViewerOpen(true);
  };
  const closeViewer = () => setViewerOpen(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        address: user.address || '',
        art_preferences: user.art_preferences || [],
      });
    }
  }, [user]);

  useEffect(() => {
    if (!isSelf) return;
    let isActive = true;

    const checkAgreement = async () => {
      try {
        const res = await getAgreementStatus([BUYER_AGREEMENT_CONFIG.agreementCode]);
        const row = res.results?.find(item => item.code === BUYER_AGREEMENT_CONFIG.agreementCode);
        if (!isActive) return;
        setAgreementAvailable(Boolean(row));
        setAgreementAccepted(row ? !!row.accepted : true);
        setAgreementCheckError(null);
      } catch (err) {
        console.error('Failed to check buyer agreement status:', err);
        if (!isActive) return;
        setAgreementAvailable(false);
        setAgreementAccepted(true);
        setAgreementCheckError('Unable to verify buyer agreement status.');
      }
    };

    checkAgreement();

    return () => {
      isActive = false;
    };
  }, [isSelf, user?.id]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMultiChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: typeof value === 'string' ? value.split(',') : value });
  };

  const handleSave = () => {
    http.put(`/buyers/${user.id}/`, {
      user: {
        first_name: formData.first_name,
        last_name: formData.last_name
      },
      address: formData.address,
      art_preferences: formData.art_preferences,
    })
      .then(() => handleClose())
      .catch(err => console.error('Failed to update profile', err));
  };

  // --- Profile images (only self) ---
  const loadProfileImages = () => {
    http.get('/api/accounts/profile-images/')
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const normalized = list.map((pi) => ({
          ...pi,
          url: pi.url || pi.image || pi.image_url || null,
        }));
        setProfileImages(normalized);
      })
      .catch((e) => {
        console.warn('Failed to load profile images (buyer)', e);
        setProfileImages([]);
      });
  };

  useEffect(() => {
    if (!isSelf || !user?.id) return;
    loadProfileImages();
  }, [isSelf, user?.id]);

  useEffect(() => {
    if (!open || !isSelf) return;
    loadProfileImages();
  }, [open, isSelf]);

  const primaryImageUrl = useMemo(() => {
    const fromCard = user?.primary_profile_image_url ? toRelativeMedia(user.primary_profile_image_url) : null;
    if (fromCard) return fromCard;
    const fromUser = user?.primary_profile_image ? toRelativeMedia(user.primary_profile_image) : null;
    if (fromUser) return fromUser;
    if (!isSelf) return null;
    const p = profileImages.find(pi => pi.is_primary);
    const src = p?.url || p?.image || p?.image_url;
    return src ? toRelativeMedia(src) : null;
  }, [user?.primary_profile_image_url, user?.primary_profile_image, profileImages, isSelf]);

  const profileViewerImages = useMemo(
    () =>
      profileImages
        .map((pi) => {
          const src = pi?.url || pi?.image || pi?.image_url;
          if (!src) return null;
          return {
            src: toRelativeMedia(src),
            caption: 'Profile',
          };
        })
        .filter(Boolean),
    [profileImages]
  );

  const onUploadProfileImages = async (files) => {
    if (!files?.length || !isSelf) return;
    const remaining = 3 - profileImages.length;
    const toSend = Array.from(files).slice(0, remaining);

    for (let i = 0; i < toSend.length; i++) {
      const fd = new FormData();
      fd.append('image', toSend[i]);
      if (!profileImages.some(p => p.is_primary) && i === 0) {
        fd.append('is_primary', 'true');
      }
      try {
        const { data } = await http.post('/api/accounts/profile-images/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setProfileImages((prev) => [data, ...prev]);
      } catch (e) {
        console.error('Buyer profile image upload failed', e);
      }
    }
  };

  const makePrimaryProfileImage = async (id) => {
    if (!isSelf) return;
    try {
      const { data } = await http.post(`/api/accounts/profile-images/${id}/make_primary/`);
      setProfileImages((prev) =>
        prev.map((p) => ({ ...p, is_primary: p.id === data.id }))
      );
    } catch (e) {
      console.error('Buyer set primary profile image failed', e);
    }
  };

  const deleteProfileImage = async (id) => {
    if (!isSelf) return;
    try {
      await http.delete(`/api/accounts/profile-images/${id}/`);
      setProfileImages((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error('Buyer delete profile image failed', e);
    }
  };

  const viewerSliderSettings = {
    dots: true,
    infinite: viewerImages.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: viewerImages.length > 1,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    pauseOnFocus: true,
    arrows: true,
  };

  return (
    <>
      <Card
        data-testid="buyer-profile-card"
        sx={{ maxWidth: 600, mb: 3, boxShadow: 3, borderRadius: 3, backgroundColor: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(16px)' }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {primaryImageUrl ? (
                <Box
                  component="img"
                  src={primaryImageUrl}
                  alt="Buyer profile"
                  referrerPolicy="no-referrer"
                  onClick={() => {
                    if (isSelf && profileViewerImages.length) {
                      const primaryIdx = profileImages.findIndex(pi => pi.is_primary);
                      const startIdx = primaryIdx >= 0 ? primaryIdx : 0;
                      openViewerWithImages(profileViewerImages, startIdx);
                    } else {
                      openViewerWithImages([{ src: primaryImageUrl, caption: 'Profile' }], 0);
                    }
                  }}
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: '50%',
                    marginRight: 12,
                    border: '1px solid #e5e7eb',
                    cursor: 'zoom-in'
                  }}
                />
              ) : (
                <Avatar sx={{ width: 64, height: 64, mr: 2 }}>
                  {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                </Avatar>
              )}

              <Box>
                <Typography data-testid="buyer-profile-name" variant="h6">{user.first_name} {user.last_name}</Typography>
                <Typography data-testid="buyer-profile-email" variant="body2" color="text.secondary">{user.email}</Typography>
                <Typography variant="body2" color="text.secondary">{user.role}</Typography>
                {isSelf && agreementAvailable && agreementAccepted === false && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="warning.main">
                      {agreementCheckError || "Accept the Buyer Membership Agreement to manage your buyer profile."}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                      onClick={() => setShowAgreementModal(true)}
                    >
                      Review
                    </Button>
                  </Box>
                )}
                <Typography data-testid="buyer-profile-art-preferences" variant="body2" color="text.secondary">
                  <strong>Art Preferences:</strong> {user.art_preferences?.join(', ') || '-'}
                </Typography>
                {user.address && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <LocationOnIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                    <Typography data-testid="buyer-profile-address" variant="body2" color="text.secondary">
                      {user.address}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {isSelf && (
              <Button variant="outlined" startIcon={<EditIcon />} size="small" onClick={handleOpen}>
                Edit
              </Button>
            )}
          </Box>
          <Divider sx={{ mt: 2 }} />
        </CardContent>
      </Card>

      {/* EDIT MODAL (self only) */}
      <Modal open={open && isSelf} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400, maxHeight: '85vh',
            overflowY: 'auto',
            bgcolor: 'background.paper', p: 4,
            boxShadow: 24, borderRadius: 2
          }}
        >
          <Typography variant="h6" mb={2}>Edit Profile</Typography>
          <Stack spacing={2}>
            {isSelf && (
              <ProfileImageManager
                profileImages={profileImages}
                maxImages={3}
                onUpload={onUploadProfileImages}
                onMakePrimary={makePrimaryProfileImage}
                onDelete={deleteProfileImage}
                onThumbnailClick={(idx) => {
                  if (!profileViewerImages.length) return;
                  openViewerWithImages(profileViewerImages, idx);
                }}
              />
            )}

            <TextField label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} fullWidth />
            <TextField label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} fullWidth />
            <TextField label="Email" name="email" value={formData.email} onChange={handleChange} fullWidth disabled />
            <TextField label="Address" name="address" value={formData.address} onChange={handleChange} fullWidth />
            <Box>
              <InputLabel>Art Preferences</InputLabel>
              <Select
                multiple
                name="art_preferences"
                value={formData.art_preferences}
                onChange={handleMultiChange}
                input={<OutlinedInput fullWidth />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => (<Chip key={val} label={val} />))}
                  </Box>
                )}
                fullWidth
              >
                {ART_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleClose} sx={{ mr: 1 }}>Cancel</Button>
              <Button variant="contained" onClick={handleSave}>Save</Button>
            </Box>
          </Stack>
        </Box>
      </Modal>

      {isSelf && agreementAvailable && (
        <AgreementAcceptModal
          open={showAgreementModal}
          onClose={() => setShowAgreementModal(false)}
          agreementCode={BUYER_AGREEMENT_CONFIG.agreementCode}
          title={BUYER_AGREEMENT_CONFIG.title}
          workflowContext={BUYER_AGREEMENT_CONFIG.workflowContext}
          onAccepted={() => {
            setAgreementAccepted(true);
            setShowAgreementModal(false);
          }}
        />
      )}

      {/* Viewer (works for self & others, just single image for others) */}
      <Modal open={viewerOpen} onClose={closeViewer}>
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Box sx={{ width: { xs: '95vw', md: '80vw' }, maxWidth: 1200 }}>
            <Slider dots arrows infinite initialSlide={viewerIndex} adaptiveHeight {...viewerSliderSettings}>
              {viewerImages.map((img, i) => (
                <Box key={i} sx={{ height: { xs: '70vh', md: '80vh' }, position: 'relative', overflow: 'hidden' }}>
                  <Box
                    component="img"
                    src={img.src}
                    alt=""
                    sx={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%',
                      objectFit: 'cover', filter: 'blur(22px)',
                      transform: 'scale(1.1)', opacity: 0.25
                    }}
                  />
                  <Box
                    component="img"
                    src={img.src}
                    alt={img.caption || 'Profile'}
                    sx={{
                      position: 'relative', maxWidth: '100%', maxHeight: '100%',
                      objectFit: 'contain', display: 'block', m: '0 auto', height: '100%'
                    }}
                  />
                  {img.caption && (
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        position: 'absolute', bottom: 12, left: 0, right: 0,
                        color: '#fff', bgcolor: 'rgba(0,0,0,0.35)', px: 1, py: 0.5
                      }}
                    >
                      {img.caption}
                    </Typography>
                  )}
                </Box>
              ))}
            </Slider>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button variant="outlined" onClick={closeViewer}>Close</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
